import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, AlertTriangle, Building2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const InvitationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await api.get(`/invitations/validate/${token}`);
        setInvitation(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invitation invalide ou expirée');
      } finally {
        setLoading(false);
      }
    };
    if (token) validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/invitations/accept/${token}`, { name, password });
      setSuccess(true);
      toast.success('Compte créé avec succès');
      // Auto-login
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        login(res.data.token, res.data.user);
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invitation invalide</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Aller à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Bienvenue sur Games Visor !</h2>
            <p className="text-slate-500">Votre compte a été créé. Redirection en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="max-w-md w-full" data-testid="invitation-form">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building2 className="h-6 w-6 text-blue-600" />
            <CardTitle className="text-xl">Rejoindre Games Visor</CardTitle>
          </div>
          {invitation?.partner_nom && (
            <p className="text-sm text-slate-500">
              Créez votre compte pour <strong>{invitation.partner_nom}</strong>
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={invitation?.email || ''} disabled className="bg-slate-50" data-testid="invitation-email" />
            </div>
            <div>
              <Label htmlFor="name">Votre nom complet *</Label>
              <Input
                id="name" value={name} onChange={e => setName(e.target.value)}
                placeholder="Prénom Nom" required data-testid="invitation-name"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe *</Label>
              <PasswordInput
                id="password" value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="6 caractères minimum" required minLength={6}
                data-testid="invitation-password"
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirmer le mot de passe *</Label>
              <PasswordInput
                id="confirm" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirmez votre mot de passe" required
                data-testid="invitation-confirm"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="invitation-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Créer mon compte
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationPage;
